import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  MapPin,
  ExternalLink,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  Send
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SkillBadge } from '@/components/ui/skill-badge';
import { ScoreRow } from '@/components/ui/score-row';
import { useAssignment } from '@/hooks/use-assignments';
import { useUpdateSavedAssignment } from '@/hooks/use-saved-assignments';
import { formatDate, formatDateRange } from '@/lib/format';
import type { SavedAssignment } from '@/hooks/use-saved-assignments';

interface SavedAssignmentSheetProps {
  saved: SavedAssignment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusLabels: Record<string, string> = {
  not_applied: 'Saved',
  applied: 'Applied',
  won: 'Won',
  lost: 'Lost'
};

const statusColors: Record<string, string> = {
  not_applied: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  applied: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  won: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  lost: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
};

function sanitizeHtml(raw: string): string {
  const hasHtml = /<(?:p|div|br|ul|ol|li|h[1-6])\b/i.test(raw);
  const formatted = hasHtml
    ? raw
    : raw
        .replace(
          /(?:^|(?<=\.\s)|(?<=<br\/?>\s*))([A-ZÅÄÖ][A-Za-zåäöÅÄÖ\s\-/&]{1,40})\s*:/g,
          '<br/><br/><strong>$1:</strong>'
        )
        .replace(/\s*([•·]\s)/g, '<br/>$1')
        .replace(/([.!?])\s{2,}([A-ZÅÄÖ])/g, '$1<br/><br/>$2')
        .replace(/^(<br\s*\/?>)+/, '');
  // DOMPurify sanitizes to prevent XSS
  return DOMPurify.sanitize(formatted);
}

export function SavedAssignmentSheet({ saved, open, onOpenChange }: SavedAssignmentSheetProps) {
  const { data: assignmentData } = useAssignment(saved?.assignmentId ?? null);
  const updateSaved = useUpdateSavedAssignment();
  const assignment = assignmentData?.data ?? null;
  const sd = assignment?.sourceData ?? null;
  const contact = sd?.contact ?? null;

  const [notes, setNotes] = useState(saved?.notes ?? '');
  const [offeredPrice, setOfferedPrice] = useState(saved?.offeredPrice ?? '');
  const [status, setStatus] = useState(saved?.status ?? 'not_applied');

  useEffect(() => {
    if (saved) {
      setNotes(saved.notes ?? '');
      setOfferedPrice(saved.offeredPrice ?? '');
      setStatus(saved.status);
    }
  }, [saved]);

  if (!saved) return null;

  function handleStatusChange(newStatus: string) {
    setStatus(newStatus);
    updateSaved.mutate({ id: saved!.id, status: newStatus });
  }

  function handleSaveFields() {
    updateSaved.mutate({
      id: saved!.id,
      notes: notes || undefined,
      offeredPrice: offeredPrice || undefined
    });
  }

  const rawText = sd?.fullDescription ?? assignment?.description ?? '';
  const sanitized = rawText ? sanitizeHtml(rawText) : '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="pb-4">
          <div className="flex items-start justify-between gap-3">
            <SheetTitle className="text-base leading-snug">{saved.assignmentTitle}</SheetTitle>
            <Badge variant="outline" className={statusColors[status] ?? statusColors.not_applied}>
              {statusLabels[status] ?? status}
            </Badge>
          </div>
          {saved.assignmentLocation && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {saved.assignmentLocation}
            </p>
          )}
        </SheetHeader>

        <div className="space-y-5">
          {/* Status actions */}
          <div className="rounded-lg border p-3 space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase">Status</h4>
            <div className="flex flex-wrap gap-2">
              {status === 'not_applied' && (
                <Button size="sm" onClick={() => handleStatusChange('applied')}>
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  Mark Applied
                </Button>
              )}
              {status === 'applied' && (
                <>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleStatusChange('won')}
                  >
                    <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
                    Won
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 dark:text-red-400"
                    onClick={() => handleStatusChange('lost')}
                  >
                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                    Lost
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground"
                    onClick={() => handleStatusChange('not_applied')}
                  >
                    Revert to Saved
                  </Button>
                </>
              )}
              {(status === 'won' || status === 'lost') && (
                <Button size="sm" variant="outline" onClick={() => handleStatusChange('applied')}>
                  Revert to Applied
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Saved {formatDate(saved.createdAt)}
              </span>
              {saved.appliedDate && (
                <span className="flex items-center gap-1">
                  <Send className="h-3 w-3" />
                  Applied {formatDate(saved.appliedDate)}
                </span>
              )}
              {saved.outcomeDate && (
                <span className="flex items-center gap-1">
                  {status === 'won' ? (
                    <CheckCircle className="h-3 w-3 text-green-500" />
                  ) : (
                    <XCircle className="h-3 w-3 text-red-500" />
                  )}
                  {status === 'won' ? 'Won' : 'Lost'} {formatDate(saved.outcomeDate)}
                </span>
              )}
            </div>
          </div>

          {/* Offered price + notes */}
          <div className="rounded-lg border p-3 space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase">Your Details</h4>
            <div className="space-y-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  <DollarSign className="mr-1 inline h-3 w-3" />
                  Offered Rate / Price
                </label>
                <input
                  type="text"
                  value={offeredPrice}
                  onChange={(e) => setOfferedPrice(e.target.value)}
                  onBlur={handleSaveFields}
                  placeholder="e.g. 950 SEK/h"
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  <FileText className="mr-1 inline h-3 w-3" />
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={handleSaveFields}
                  placeholder="Add personal notes..."
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring resize-none"
                />
              </div>
            </div>
          </div>

          {/* Match scores */}
          {saved.matchScore && (
            <div className="rounded-lg border p-3 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">
                Match Scores
              </h4>
              <ScoreRow
                aiScore={saved.matchScore}
                heuristicScore={saved.heuristicScore ?? null}
                teamFitScore={saved.teamFitScore ?? null}
                layout="inline"
              />
            </div>
          )}

          {/* Assignment metadata */}
          {assignment && (
            <div className="rounded-lg border p-3 space-y-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">
                Assignment Details
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {sd?.company && (
                  <div>
                    <span className="text-xs text-muted-foreground">Company</span>
                    <p className="font-medium">{sd.company}</p>
                  </div>
                )}
                {(sd?.startDate || sd?.endDate) && (
                  <div>
                    <span className="text-xs text-muted-foreground">Period</span>
                    <p className="font-medium">{formatDateRange(sd.startDate, sd.endDate)}</p>
                  </div>
                )}
                {sd?.duration && (
                  <div>
                    <span className="text-xs text-muted-foreground">Duration</span>
                    <p className="font-medium">{sd.duration}</p>
                  </div>
                )}
                {sd?.scope && (
                  <div>
                    <span className="text-xs text-muted-foreground">Scope</span>
                    <p className="font-medium">{sd.scope}</p>
                  </div>
                )}
                {sd?.rate && (
                  <div>
                    <span className="text-xs text-muted-foreground">Listed Rate</span>
                    <p className="font-medium text-orange-600 dark:text-orange-400">{sd.rate}</p>
                  </div>
                )}
                {sd?.level && (
                  <div>
                    <span className="text-xs text-muted-foreground">Level</span>
                    <p className="font-medium">{sd.level}</p>
                  </div>
                )}
                {sd?.remoteness && (
                  <div>
                    <span className="text-xs text-muted-foreground">Remote</span>
                    <p className="font-medium">{sd.remoteness}</p>
                  </div>
                )}
                {assignment.status && (
                  <div>
                    <span className="text-xs text-muted-foreground">Status</span>
                    <p className="font-medium capitalize">{assignment.status}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Skills */}
          {assignment && assignment.skills.length > 0 && (
            <div className="rounded-lg border p-3 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">
                Required Skills ({assignment.skills.length})
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {assignment.skills.map((s) => (
                  <SkillBadge
                    key={s.id}
                    skill={s.skill}
                    variant={s.isMustHave ? 'match' : 'neutral'}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Description — sanitized with DOMPurify to prevent XSS */}
          {sanitized && (
            <div className="rounded-lg border p-3 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">Description</h4>
              <div
                className="prose prose-sm max-w-none text-foreground [&_strong]:font-semibold [&_br]:block [&_br]:mt-1"
                dangerouslySetInnerHTML={{ __html: sanitized }}
              />
            </div>
          )}

          {/* Source link */}
          {sd?.url && (
            <a
              href={sd.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              View on {sd.platform ?? assignment?.source}
            </a>
          )}

          {/* Contact */}
          {contact && (contact.name || contact.email || contact.phone) && (
            <div className="rounded-lg border p-3 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">Contact</h4>
              <div className="flex items-center gap-3">
                {contact.profilePictureUrl && (
                  <img
                    src={contact.profilePictureUrl}
                    alt={contact.name ?? 'Contact'}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                )}
                <div className="space-y-0.5 text-xs">
                  {contact.name && <p className="font-medium">{contact.name}</p>}
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Mail className="h-3 w-3" />
                      {contact.email}
                    </a>
                  )}
                  {contact.phone && (
                    <p className="flex items-center gap-1 text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      {contact.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
